import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Shield } from "lucide-react";

const ROLES = [
    { v: "administrator", label: "Administrator" },
    { v: "full_access", label: "Full access" },
    { v: "read_write", label: "Read & write" },
    { v: "read_only", label: "Read only" },
    { v: "data_entry_only", label: "Data entry only" },
];
const DEPTS = ["Management", "Purchase", "HR & Admin", "Accounts"];
const EMPTY = { email: "", password: "", name: "", role: "read_only", department: "Purchase", can_approve_order_awards: false, active: true };

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [show, setShow] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [err, setErr] = useState("");

    const load = async () => { const { data } = await api.get("/users"); setUsers(data); };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setErr("");
        try {
            if (editId) {
                const p = { ...form };
                if (!p.password) delete p.password;
                await api.put(`/users/${editId}`, p);
            } else {
                await api.post("/users", form);
            }
            setShow(false); setForm(EMPTY); setEditId(null); load();
        } catch (err) { setErr(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    };
    const del = async (u) => {
        if (!confirm(`Delete user ${u.name}?`)) return;
        try { await api.delete(`/users/${u.id}`); load(); } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    return (
        <div data-testid="users-page" className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Admin</div>
                    <h1 className="text-3xl font-heading font-semibold text-stone-900">Users</h1>
                </div>
                <button data-testid="new-user-btn" onClick={() => { setForm(EMPTY); setEditId(null); setShow(true); }} className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2"><Plus size={15} /> New user</button>
            </div>

            <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                            <th className="text-left py-3 px-4">Name</th>
                            <th className="text-left py-3 px-4">Email</th>
                            <th className="text-left py-3 px-4">Role</th>
                            <th className="text-left py-3 px-4">Department</th>
                            <th className="text-left py-3 px-4">Approver</th>
                            <th className="text-left py-3 px-4">Active</th>
                            <th className="text-right py-3 px-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <tr key={u.id} className="border-t border-stone-100 hover:bg-stone-50">
                                <td className="py-3 px-4 font-medium text-stone-900">{u.name}</td>
                                <td className="py-3 px-4 text-stone-700">{u.email}</td>
                                <td className="py-3 px-4 text-stone-700 capitalize">{u.role?.replace("_", " ")}</td>
                                <td className="py-3 px-4 text-stone-700">{u.department}</td>
                                <td className="py-3 px-4">
                                    {u.can_approve_order_awards ? <span className="inline-flex items-center gap-1 text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5 text-xs"><Shield size={11} /> Yes</span> : <span className="text-stone-400 text-xs">No</span>}
                                </td>
                                <td className="py-3 px-4">
                                    <span className={"text-xs px-2 py-0.5 rounded-md border " + (u.active ? "bg-green-50 border-green-200 text-green-700" : "bg-stone-100 border-stone-200 text-stone-500")}>
                                        {u.active ? "Active" : "Disabled"}
                                    </span>
                                </td>
                                <td className="py-3 px-4 text-right">
                                    <button onClick={() => { setForm({ ...u, password: "" }); setEditId(u.id); setShow(true); }} className="text-xs text-amber-700 hover:text-amber-900 mr-3">Edit</button>
                                    {u.id !== me.id && <button onClick={() => del(u)} className="text-xs text-red-700 hover:text-red-900">Delete</button>}
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-stone-500 text-sm">No users.</td></tr>}
                    </tbody>
                </table>
            </div>

            {show && (
                <div className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4">
                    <div className="bg-white border border-stone-200 rounded-xl max-w-2xl w-full">
                        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
                            <div className="font-heading font-semibold text-stone-900">{editId ? "Edit user" : "New user"}</div>
                            <button onClick={() => setShow(false)} className="text-stone-500"><X size={18} /></button>
                        </div>
                        <form onSubmit={submit} className="p-6 space-y-4">
                            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="overline block mb-1">Name *</label>
                                    <input data-testid="u-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                                </div>
                                <div>
                                    <label className="overline block mb-1">Email *</label>
                                    <input data-testid="u-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" disabled={!!editId} />
                                </div>
                                <div>
                                    <label className="overline block mb-1">{editId ? "New password (leave blank to keep)" : "Password (auto if blank)"}</label>
                                    <input data-testid="u-password" type="text" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3 font-mono" />
                                </div>
                                <div>
                                    <label className="overline block mb-1">Role *</label>
                                    <select data-testid="u-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3">
                                        {ROLES.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="overline block mb-1">Department *</label>
                                    <select data-testid="u-dept" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3">
                                        {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                                <div className="flex items-center gap-6 pt-6">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input data-testid="u-approver" type="checkbox" checked={!!form.can_approve_order_awards} onChange={(e) => setForm({ ...form, can_approve_order_awards: e.target.checked })} />
                                        Can approve order awards
                                    </label>
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                                        Active
                                    </label>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShow(false)} className="text-sm text-stone-600 px-4 py-2 rounded-md">Cancel</button>
                                <button data-testid="save-user" type="submit" className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
